/**
 * authApi — login/profile.
 * parseUserProfileResponse: interceptor đã trả { success, data } (không nested data.success).
 * requestUserProfile(id, phanHe?) — truyền phanHe cho API legacy; contract mới có thể trả đủ module.
 */
import {
  ChangePasswordPayload,
  LockedLogin,
  LoginPayload,
  LoginResponse,
  LoginStatusResponse,
  ProfileUser,
  SsoPublicConfig,
} from '@/types/authen';
import type { ApiResponse } from '@/types/base';
// import ENVIRONMENT from '../../../environment/env';
import request from '../base-service';
import { InternalAxiosRequestConfig } from 'axios';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    authorization?: boolean;
  }
}

const BASE_URL = '/v1/auth';
const ORG_BASE_URL = '/v1';

export interface CrossSiteHandoffResponse {
  redirectUrl?: string | null;
  RedirectUrl?: string | null;
}

export interface CrossSiteExchangeResponse {
  accessToken?: string;
  AccessToken?: string;
  refreshToken?: string;
  RefreshToken?: string;
  tenantID?: string;
  TenantID?: string;
  phanHe?: string;
  PhanHe?: string;
  message?: string;
  Message?: string;
  returnUrl?: string | null;
  ReturnUrl?: string | null;
}

/** base-service interceptor trả thẳng ApiResponse { success, data } */
export function parseUserProfileResponse(response: unknown): ProfileUser | null {
  if (!response || typeof response !== 'object') return null;
  const res = response as { success?: boolean; data?: ProfileUser };
  if (res.success && res.data) return res.data;
  return null;
}

export const authApi = {
    requestLoginService: (payload: LoginPayload, response?: LoginResponse) =>
        request.post(`${BASE_URL}/login`, { ...payload, response }),
    /** Thu hồi session hiện tại trên BE (jti + sid). Gọi trước khi clearAuthAll. */
    logout: () =>
        request.post(`${BASE_URL}/logout`, {}, {
            authorization: true,
            silentError: true,
        } as InternalAxiosRequestConfig),
    getLoginStatus: (email: string) =>
        request.get(`${BASE_URL}/login/status`, {
            authorization: false,
            silentError: true,
            params: { email },
        } as InternalAxiosRequestConfig) as Promise<LoginStatusResponse>,
    refreshLoginCaptcha: (email: string) =>
        request.get(`${BASE_URL}/login/captcha`, {
            authorization: false,
            silentError: true,
            params: { email },
        } as InternalAxiosRequestConfig) as Promise<LoginStatusResponse>,
    listLoginLocks: () =>
        request.get(`${BASE_URL}/login/locks`, {
            authorization: true,
        } as InternalAxiosRequestConfig) as Promise<ApiResponse<LockedLogin[]>>,
    unlockLogin: (loginKey: string) =>
        request.post(`${BASE_URL}/login/locks/unlock`, { loginKey }, {
            authorization: true,
        } as InternalAxiosRequestConfig) as Promise<ApiResponse<object>>,
    requestSwitchService: (payload: LoginPayload, response?: LoginResponse) =>
        request.post(`${BASE_URL}/switch`, { ...payload, response }),
    requestCrossSiteHandoff: (accessToken: string, tenantID: string, returnUrl?: string | null) =>
        request.post<CrossSiteHandoffResponse>(`${BASE_URL}/cross-site/handoff`, { returnUrl }, {
            authorization: true,
            accessTokenOverride: accessToken,
            tenantIdOverride: tenantID,
            silentError: true,
            rejectOnError: true,
        } as InternalAxiosRequestConfig),
    exchangeCrossSiteHandoff: (handoffCode: string) =>
        request.post<CrossSiteExchangeResponse>(`${BASE_URL}/cross-site/exchange`, { handoffCode }, {
            authorization: false,
            silentError: true,
            rejectOnError: true,
        } as InternalAxiosRequestConfig),
    getSsoConfig: () =>
        request.get(`${BASE_URL}/sso/config`, {
            authorization: false,
        } as InternalAxiosRequestConfig) as Promise<SsoPublicConfig>,
    loginGoogleSso: (idToken: string) =>
        request.post(`${BASE_URL}/sso/google`, { idToken }, {
            authorization: false,
        } as InternalAxiosRequestConfig),
    loginMicrosoftSso: (idToken: string) =>
        request.post(`${BASE_URL}/sso/microsoft`, { idToken }, {
            authorization: false,
        } as InternalAxiosRequestConfig),
    requestUserProfile: (
        id: string,
        phanHe?: string,
        options?: { silentError?: boolean; silentNotFound?: boolean; rejectOnError?: boolean },
    ) =>
        request.get(`${ORG_BASE_URL}/employee/profile/${id}`, {
            authorization: true,
            params: phanHe ? { phanHe } : undefined,
            phanHeOverride: phanHe,
            silentError: options?.silentError,
            silentNotFound: options?.silentNotFound,
            rejectOnError: options?.rejectOnError,
        } as InternalAxiosRequestConfig),
    refeshToken: (refreshToken: string) => {
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }
        return request.post(`${BASE_URL}/refresh`, { refreshToken: refreshToken });
    },
    changePassword: (payload: ChangePasswordPayload) =>
        request.post(`${ORG_BASE_URL}/employee/changepassword`, payload, {
            authorization: true,
        } as InternalAxiosRequestConfig),
    forgotPassword: (email: string) =>
        request.post(`${BASE_URL}/forgot-password`, { email }),
    resetPassword: (payload: { email: string; otpCode: string; newPassword: string }) =>
        request.post(`${BASE_URL}/reset-password`, payload),
}
